import * as fabric from 'office-ui-fabric-react';
import { IDropdownStyles } from 'office-ui-fabric-react';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import ListItemCreateButton from '../../../../../components/custom/ListItemCreateButton';
import { useConfirmation } from '../../../../../components/dialogs';
import { useFetchedValue } from '../../../../../components/hooks/useFetched';
import { usePageableGrid } from '../../../../../components/hooks/usePageableGrid';
import { Grid, GridColumn } from '../../../../../components/KendoReact/Grid';
import { useWindow } from '../../../../../components/windows/WindowContext';
import * as enums from '../../../../../enums';
import { ticketsNumerationType } from '../../../../../enums';
import { IServiceListItem } from '../../../../../models/classes/IService';
import AdministrationActionCell from '../../components/AdministrationActionCell';
import HoursCell from '../components/HoursCell';
import ServiceCreateModalWindow from './ServiceCreateModalWindow';
import ServiceEditModalWindow from './ServiceEditModalWindow';

const options: fabric.IChoiceGroupOption[] = [
    {
        key: '1',
        text: enums.ticketsNumerationType.format(1)
    },
    {
        key: '2',
        text: enums.ticketsNumerationType.format(2)
    }
];

const getServices = async (organizationInformationId: number, skip: number, take: number) =>
    api.service.getServices({
        organizationInformationId,
        skip,
        take
    });

const getOrganizationTicketsNumerationType = async (organizationInformationId: number) =>
    api.administration.getOrganizationTicketsNumerationType(organizationInformationId);

export const dropdownStyles: Partial<IDropdownStyles> = { dropdown: { width: 300 } };

const ServicesPage: FC = () => {
    const { organizationInformationId } = useParams<'organizationInformationId'>();

    const [gridPageProps] = usePageableGrid([5, 10, 15, 20, 25], 15);
    const { openWindow } = useWindow();

    const [isLoading, setIsLoading] = useState(false);
    const { confirmAsync } = useConfirmation();

    const [services, refetchServices, , , isServicesLoading] = useFetchedValue(
        getServices,
        true,
        parseInt(organizationInformationId.toString()),
        gridPageProps.skip,
        gridPageProps.take
    );

    const [ticketsNumerationTypeResponse, refetchTicketsNumerationTypeResponse, , , ,] = useFetchedValue(
        getOrganizationTicketsNumerationType,
        true,
        parseInt(organizationInformationId.toString())
    );

    const openServiceEditModalWindow = async (serviceId: number) => {
        try {
            ServiceEditModalWindow.defaultProps = {
                componentProps: {
                    serviceId: serviceId,
                    organizationInformationId: parseInt(organizationInformationId.toString()),
                    refetchServices: refetchServices
                }
            };
            openWindow({
                title: 'Paslaugos redagavimas',
                component: ServiceEditModalWindow,
                isDialogBehaviour: true,
                width: 1280,
                height: 720
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const openServiceCreateModalWindow = async (basedOnInitialValues: IServiceListItem | null) => {
        try {
            ServiceCreateModalWindow.defaultProps = {
                componentProps: {
                    organizationIndormationId: parseInt(organizationInformationId.toString()),
                    refetchData: refetchServices,
                    basedOnInitialValues: basedOnInitialValues,
                    openServiceCreateModalWindow: openServiceCreateModalWindow
                }
            };
            openWindow({
                title: 'Paslaugos kūrimas',
                component: ServiceCreateModalWindow,
                isDialogBehaviour: true,
                width: 1280,
                height: 720
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const deleteService = async (id: number) => {
        try {
            setIsLoading(true);
            await api.service.deleteService(id);
            refetchServices();
            toast.success('Paslauga sėkmingai ištrinta');
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmitClick = async (newNumerationType: string) => {
        const { success } = await confirmAsync(
            'Įrašo ištrynimas',
            'Ar tikrai norite pakeist bilietėlių numeracijos tipą? Išsaugojus pakeitimus, numeracijos būdas pasikeis iškart,' +
                ' todėl rekomenduojama tai atlikti ne darbo metu'
        );

        if (!success) {
            return;
        }
        try {
            setIsLoading(true);
            await api.administration.updateOrganizationTicketsNumerationType({
                id: parseInt(organizationInformationId.toString()),
                ticketsNumerationType: newNumerationType === '1' ? ticketsNumerationType.ServiceNumeration : ticketsNumerationType.GeneralNumeration
            });
            toast.success('Bilietėlių numeracijos tipas sėkmingai pakeistas');
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            refetchTicketsNumerationTypeResponse();
            setIsLoading(false);
        }
    };

    return (
        <div>
            {isLoading && <KendoLoading />}
            {ticketsNumerationTypeResponse && (
                <fabric.Stack horizontal horizontalAlign="space-between">
                    <fabric.ChoiceGroup
                        selectedKey={ticketsNumerationTypeResponse.toString()}
                        options={options}
                        onChange={async ({}: any, item) => await handleSubmitClick(item!.key)}
                        label="Bilietų numeracijos tipas:"
                        styles={{
                            label: {
                                textAlign: 'left'
                            }
                        }}
                    />
                    <ListItemCreateButton openCreateModal={() => openServiceCreateModalWindow(null)} />
                </fabric.Stack>
            )}

            <div>
                <Grid className="mt-2" {...gridPageProps} data={services} isLoading={isServicesLoading}>
                    <GridColumn
                        width={200}
                        title="Veiksmai"
                        cell={(c) => (
                            <AdministrationActionCell
                                {...c}
                                fetchData={refetchServices}
                                deleteItem={deleteService}
                                editItem={openServiceEditModalWindow}
                            />
                        )}
                    />
                    <GridColumn width={200} title="Pavadinimas" field="name_LT" />
                    <GridColumn width={150} title="Darbo laiko pradžia" cell={(c) => <HoursCell {...c} hours={c.dataItem.startWorkingHours} />} />
                    <GridColumn width={150} title="Darbo laiko pabaiga" cell={(c) => <HoursCell {...c} hours={c.dataItem.endWorkingHours} />} />
                    <GridColumn width={150} title="Bilieto priešdėlis" field="ticketNumberPrefix" />
                    <GridColumn width={150} title="Bilieto numeris nuo" field="ticketNumberRangeFrom" />
                    <GridColumn title="Bilieto numeris iki" field="ticketNumberRangeTo" />
                </Grid>
            </div>
        </div>
    );
};
export default ServicesPage;
